import React from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Store,
  CheckCircle,
  Clock,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  HeadphonesIcon,
  XCircle,
  Activity,
  RefreshCw,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { dashboardApi } from '../../lib/api'
import StatCard from '../../components/shared/StatCard'
import LoadingState from '../../components/shared/LoadingState'
import ErrorState from '../../components/shared/ErrorState'
import { formatMoney, formatDatetime, formatRelativeTime } from '../../lib/utils'
import type { DashboardSummary } from '../../types'
import { tx } from '../../lib/i18n'

const EMPTY_SUMMARY: DashboardSummary = {
  totalShops: 0,
  activeShops: 0,
  trialShops: 0,
  expiredShops: 0,
  suspendedShops: 0,
  mrr: 0,
  todayRevenue: 0,
  failedPayments: 0,
  openTickets: 0,
  recentActivity: [],
  systemHealth: [],
}

const actionLabel = (action: string) =>
  action
    .replace(/\./g, ' › ')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

const healthStatusColor = (s: string) => {
  if (s === 'healthy') return 'text-green-600 bg-green-100'
  if (s === 'degraded') return 'text-amber-600 bg-amber-100'
  return 'text-red-600 bg-red-100'
}

const DashboardPage: React.FC = () => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['dashboard-summary'],
    queryFn: () => dashboardApi.getSummary().then((r) => r.data.data),
    refetchInterval: 60000,
  })

  const summary = data ?? EMPTY_SUMMARY
  const barData = [
    { status: tx('Active'), count: summary.activeShops },
    { status: tx('Trial'), count: summary.trialShops },
    { status: tx('Suspended'), count: summary.suspendedShops },
    { status: tx('Cancelled'), count: summary.expiredShops },
  ]

  if (isLoading) return <LoadingState variant="spinner" message={tx('Loading dashboard...')} />
  if (error && !data)
    return <ErrorState message={tx('Failed to load dashboard.')} onRetry={() => refetch()} />

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{tx('Dashboard')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {tx('Last updated:')} {formatDatetime(new Date().toISOString())}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          {tx('Refresh')}
        </button>
      </div>

      {/* Row 1 — Shop stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          title={tx('Total Shops')}
          value={summary.totalShops.toLocaleString()}
          icon={Store}
          iconColor="bg-indigo-100 text-indigo-600"
        />
        <StatCard
          title={tx('Active Shops')}
          value={summary.activeShops.toLocaleString()}
          icon={CheckCircle}
          iconColor="bg-green-100 text-green-600"
        />
        <StatCard
          title={tx('Trial Shops')}
          value={summary.trialShops.toLocaleString()}
          icon={Clock}
          iconColor="bg-blue-100 text-blue-600"
        />
        <StatCard
          title={tx('Monthly Revenue')}
          value={formatMoney(summary.mrr)}
          subtitle="MRR"
          icon={DollarSign}
          iconColor="bg-emerald-100 text-emerald-600"
        />
        <StatCard
          title={tx('Today Revenue')}
          value={formatMoney(summary.todayRevenue)}
          icon={TrendingUp}
          iconColor="bg-teal-100 text-teal-600"
        />
      </div>

      {/* Row 2 — Alert stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          title={tx('Failed Payments')}
          value={summary.failedPayments}
          subtitle={tx('Needs attention')}
          icon={XCircle}
          iconColor="bg-red-100 text-red-600"
        />
        <StatCard
          title={tx('Open Tickets')}
          value={summary.openTickets}
          subtitle={tx('Support tickets')}
          icon={HeadphonesIcon}
          iconColor="bg-orange-100 text-orange-600"
        />
        <StatCard
          title={tx('Suspended Shops')}
          value={summary.suspendedShops}
          icon={AlertTriangle}
          iconColor="bg-amber-100 text-amber-600"
        />
      </div>

      {/* Row 3 — Bar chart */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">{tx('Shops by Status')}</h2>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={barData} barSize={40}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis dataKey="status" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: 12 }}
            />
            <Bar dataKey="count" fill="#22c55e" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Row 4 — Activity + Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">{tx('Recent Activity')}</h2>
          <div className="space-y-3">
            {summary.recentActivity.slice(0, 10).map((log) => (
              <div key={log.id} className="flex items-start gap-3">
                <div className="h-7 w-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Activity className="h-3.5 w-3.5 text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 font-medium truncate">{actionLabel(log.action)}</p>
                  <p className="text-xs text-gray-400">
                    by {log.actorName ?? 'System'} · {formatRelativeTime(log.createdAt)}
                  </p>
                </div>
              </div>
            ))}
            {summary.recentActivity.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">{tx('No recent activity.')}</p>
            )}
          </div>
        </div>

        {/* System Health */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-sm font-semibold text-gray-700 mb-4">{tx('System Health')}</h2>
          <div className="space-y-3">
            {summary.systemHealth.map((svc) => (
              <div key={svc.service} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span
                    className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${
                      svc.status === 'healthy'
                        ? 'bg-green-500'
                        : svc.status === 'degraded'
                        ? 'bg-amber-500'
                        : 'bg-red-500'
                    }`}
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{svc.service}</p>
                    {svc.message && <p className="text-xs text-amber-600">{svc.message}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {svc.latencyMs !== null && (
                    <span className="text-xs text-gray-400 font-mono">{svc.latencyMs}ms</span>
                  )}
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full ${healthStatusColor(svc.status)}`}
                  >
                    {svc.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
